import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { resolveProfileImage, initialsFor } from '../utils/profileImage';

/**
 * Official CCUK admin/staff identities use the CCUK blue avatar treatment.
 *
 * Important:
 * - Normal member roles keep the existing pale fallback.
 * - An admin who is currently represented as Talent/Agent/etc is NOT
 *   automatically treated as an admin merely because they own admin access.
 * - Staff records are identified through their staff fields.
 */
function isAdminStaffIdentity(user) {
  if (!user) return false;

  const role =
    user.active_role ||
    user.activeRole ||
    user.current_role ||
    user.currentRole ||
    user.role ||
    '';

  // When role context exists, it is authoritative.
  // This prevents a multi-role admin from appearing as staff
  // while actively using Talent, Agent, Production, etc.
  if (role) {
    return (
      role === 'admin' ||
      role === 'staff' ||
      role === 'support'
    );
  }

  // Fallback for standalone internal staff records where no
  // active role has been supplied to the avatar component.
  return (
    Boolean(user.staff_title) ||
    Boolean(user.staffTitle) ||
    Boolean(user.is_master_admin) ||
    Boolean(user.isMasterAdmin)
  );
}

/**
 * UserAvatar — shared avatar component.
 *
 * Official admin/staff styling:
 *   background: #002FA7
 *   initials: white
 *
 * Standard users retain the existing pale CCUK blue fallback.
 */
export default function UserAvatar({
  user,
  className = 'h-9 w-9',
  fallbackClassName,
  nameOverride,
  alt,
  ...rest
}) {
  const src = resolveProfileImage(user);

  const name =
    nameOverride ||
    user?.admin_display_name ||
    user?.staff_name ||
    user?.display_name ||
    user?.name ||
    user?.full_name ||
    user?.email ||
    '';

  const adminStaff = isAdminStaffIdentity(user);

  const resolvedFallbackClassName =
    fallbackClassName ||
    (
      adminStaff
        ? 'bg-[#002FA7] text-white text-xs font-semibold'
        : 'bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold'
    );

  const resolvedAvatarClassName = adminStaff
    ? `${className} bg-[#002FA7]`
    : className;

  return (
    <Avatar className={resolvedAvatarClassName} {...rest}>
      {src ? (
        <AvatarImage
          src={src}
          alt={alt || name || 'avatar'}
          className={adminStaff ? 'bg-[#002FA7]' : undefined}
        />
      ) : null}

      <AvatarFallback className={resolvedFallbackClassName}>
        {initialsFor(name)}
      </AvatarFallback>
    </Avatar>
  );
}
